import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, PhotoIcon } from '@heroicons/react/24/outline';

interface AnimePhoto {
  originalUrl: string;
  animeUrl: string;
  timestamp: Date;
  fileName: string;
}

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

const Header: React.FC<{
  showHistory: boolean;
  onToggleHistory: () => void;
}> = ({ showHistory, onToggleHistory }) => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center space-x-4">
          <Link
            to="/generation"
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="h-3 w-3 bg-pink-500 rounded-full animate-pulse" />
            <h1 className="text-xl font-bold text-white">Photo to Anime</h1>
          </div>
        </div>

        <button
          onClick={onToggleHistory}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            showHistory
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <ClockIcon className="w-5 h-5" />
          <span className="text-sm font-medium">History</span>
        </button>
      </div>
    </div>
  </div>
);

const PhotoUploader: React.FC<{
  onFileSelect: (file: File) => void;
  loading: boolean;
}> = ({ onFileSelect, loading }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      onFileSelect(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div className="space-y-6">
      <div
        className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          className="hidden"
        />
        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
        <div className="mt-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Select Photo
          </button>
          <p className="mt-2 text-sm text-gray-400">or drag and drop</p>
          <p className="mt-1 text-xs text-gray-500">PNG, JPG, WEBP up to 10MB</p>
        </div>
      </div>
    </div>
  );
};

const PhotoComparison: React.FC<{
  originalUrl: string;
  animeUrl: string;
  onDownload: () => void;
}> = ({ originalUrl, animeUrl, onDownload }) => (
  <div className="bg-gray-800 rounded-lg overflow-hidden">
    <div className="grid grid-cols-2 gap-4 p-4">
      <div>
        <p className="text-sm font-medium text-gray-400 mb-2">Original Photo</p>
        <img src={originalUrl} alt="Original" className="w-full rounded-lg" />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-400 mb-2">Anime Style</p>
        <img src={animeUrl} alt="Anime" className="w-full rounded-lg" />
      </div>
    </div>
    <div className="p-4 border-t border-gray-700">
      <button
        onClick={onDownload}
        className="w-full py-2 px-4 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
      >
        Download Anime Version
      </button>
    </div>
  </div>
);

const HistorySidebar: React.FC<{
  show: boolean;
  items: AnimePhoto[];
  onClose: () => void;
  onDelete: (index: number) => void;
  onDownload: (photo: AnimePhoto) => void;
}> = ({ show, items, onClose, onDelete, onDownload }) => (
  <div
    className={`fixed inset-y-0 right-0 w-96 bg-gray-900 border-l border-gray-700 transform transition-transform duration-300 ease-in-out ${
      show ? 'translate-x-0' : 'translate-x-full'
    }`}
  >
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-medium text-white">History</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {items.map((item, index) => (
          <div key={index} className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="grid grid-cols-2 gap-2 p-2">
              <img src={item.originalUrl} alt="Original" className="w-full h-24 object-cover rounded" />
              <img src={item.animeUrl} alt="Anime" className="w-full h-24 object-cover rounded" />
            </div>
            <div className="p-3 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500">{formatDate(item.timestamp)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onDownload(item)}
                    className="p-1.5 bg-green-500/20 hover:bg-green-500/40 rounded-lg text-green-400 hover:text-green-300 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDelete(index)}
                    className="p-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-800 mb-4">
              <ClockIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-300 mb-2">No photos converted yet</h3>
            <p className="text-gray-500">Upload a photo to get started</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const PhotoToAnime: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AnimePhoto[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleConvert = async () => {
    if (!selectedFile || loading) return;

    setLoading(true);
    let fileUrl: string | null = null;
    try {
      // Create local URL for the file
      fileUrl = URL.createObjectURL(selectedFile);

      // Convert to anime using the file URL
      const response = await fetch(
        `https://api.nexon.my.id/api/toanime?url=${encodeURIComponent(fileUrl)}&apikey=${process.env.REACT_APP_NEXON_API_KEY}`
      );

      if (!response.ok) {
        throw new Error('Failed to convert image to anime');
      }

      const data = await response.json();
      if (data && data.status) {
        const newItem: AnimePhoto = {
          originalUrl: fileUrl,
          animeUrl: data.data.url,
          timestamp: new Date(),
          fileName: `anime_${selectedFile.name}`
        };
        
        setItems(prev => [newItem, ...prev]);
        setSelectedFile(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error converting photo:', err);
    } finally {
      setLoading(false);
      // Clean up the object URL
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    }
  };

  const handleDownload = async (photo: AnimePhoto) => {
    try {
      const response = await fetch(photo.animeUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nexon-anime_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading photo:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200">
      <Header showHistory={showHistory} onToggleHistory={() => setShowHistory(!showHistory)} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Photo to Anime Converter</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Transform your photos into beautiful anime-style artwork.
            Upload your photo and watch the magic happen!
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-8">
          <PhotoUploader
            onFileSelect={handleFileSelect}
            loading={loading}
          />

          {selectedFile && (
            <div className="text-center">
              <button
                onClick={handleConvert}
                disabled={loading}
                className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                  loading
                    ? 'bg-pink-500/50 text-pink-200 cursor-not-allowed'
                    : 'bg-pink-500 text-white hover:bg-pink-600'
                }`}
              >
                {loading ? 'Converting...' : 'Convert to Anime'}
              </button>
            </div>
          )}

          <div className="space-y-4">
            {items.map((item, index) => (
              <PhotoComparison
                key={index}
                originalUrl={item.originalUrl}
                animeUrl={item.animeUrl}
                onDownload={() => handleDownload(item)}
              />
            ))}
          </div>
        </div>
      </div>

      <HistorySidebar
        show={showHistory}
        items={items}
        onClose={() => setShowHistory(false)}
        onDelete={(index) => setItems(items.filter((_, i) => i !== index))}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default PhotoToAnime;
